import React, { useEffect, useMemo, useState } from "react";
import { createFixedQuiz, createSyntheticQuiz, fetchAllData } from "./quiz3Utils";

// QA-06 (đề cố định) / QA-06b (đề tổng hợp) — gắn với node (subjectId,
// topicId) đang chọn ở cây.
const Quiz3QuizForm = ({ uid, subjectId, topicId, onDone }) => {
  const [type, setType] = useState("fixed");
  const [title, setTitle] = useState("");
  const [numQuestions, setNumQuestions] = useState(20);
  const [timeLimitMinutes, setTimeLimitMinutes] = useState(12);
  const [timePerQuestionSeconds, setTimePerQuestionSeconds] = useState("");
  // "both" (mặc định): hiện thường, cả câu hỏi và đáp án cùng lúc;
  // "animated": chống chụp ảnh AI — câu hỏi chạy dần rồi mới hiện đáp án (phải chủ động chọn).
  const [displayMode, setDisplayMode] = useState("both");
  const [questions, setQuestions] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const loadQuestions = React.useCallback(async () => {
    const data = await fetchAllData();
    setQuestions(data.questions || {});
  }, []);

  useEffect(() => {
    loadQuestions().catch(() => {});
  }, [loadQuestions]);

  const availableQuestions = useMemo(() => {
    return Object.entries(questions)
      .map(([id, q]) => ({ id, ...q }))
      .filter((q) => q.subjectId === subjectId)
      .filter((q) => (topicId ? q.topicId === topicId : true))
      .filter((q) => !q.fixedQuizId); // ràng buộc: 1 câu hỏi chỉ thuộc tối đa 1 đề cố định
  }, [questions, subjectId, topicId]);

  const toggleSelect = (id) => {
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");
    if (!title.trim()) {
      setError("Vui lòng đặt tên đề.");
      return;
    }
    if (type === "fixed" && selectedIds.length === 0) {
      setError("Chọn ít nhất 1 câu hỏi cho đề cố định.");
      return;
    }
    setSaving(true);
    try {
      const timeLimitSeconds = timeLimitMinutes ? Number(timeLimitMinutes) * 60 : null;
      const perQuestion = timePerQuestionSeconds ? Number(timePerQuestionSeconds) : null;
      if (type === "fixed") {
        await createFixedQuiz({
          title: title.trim(),
          subjectId,
          topicId,
          timeLimitSeconds,
          timePerQuestionSeconds: perQuestion,
          questionIds: selectedIds,
          displayMode,
          uid,
        });
      } else {
        await createSyntheticQuiz({
          title: title.trim(),
          subjectId,
          topicId,
          numQuestions: Number(numQuestions) || 20,
          timeLimitSeconds,
          timePerQuestionSeconds: perQuestion,
          displayMode,
          uid,
        });
      }
      setSuccessMsg("Đã tạo đề thành công.");
      setTitle("");
      setSelectedIds([]);
      await loadQuestions(); // câu vừa gắn vào đề cố định sẽ biến khỏi danh sách khả dụng
      onDone && onDone();
    } catch (err) {
      setError(err.message || "Có lỗi xảy ra, thử lại.");
    } finally {
      setSaving(false);
    }
  };

  if (!subjectId) {
    return <p style={{ color: "#6b7280" }}>Chọn 1 môn học/chủ đề ở cây bên trái để tạo đề.</p>;
  }

  return (
    <form onSubmit={handleSubmit} style={cardStyle}>
      <h3 style={{ marginTop: 0 }}>Tạo đề thi</h3>

      <label style={labelStyle}>Loại đề</label>
      <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="radio" checked={type === "fixed"} onChange={() => setType("fixed")} /> Đề cố định
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="radio" checked={type === "synthetic"} onChange={() => setType("synthetic")} /> Đề tổng hợp
          (random)
        </label>
      </div>

      <label style={labelStyle}>Chế độ hiển thị khi làm bài</label>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="radio" checked={displayMode === "both"} onChange={() => setDisplayMode("both")} /> Hiện thường: hiện
          cả câu hỏi và đáp án cùng lúc
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="radio" checked={displayMode === "animated"} onChange={() => setDisplayMode("animated")} /> Chống chụp ảnh
          AI: câu hỏi chạy dần, xong mới hiện các đáp án
        </label>
      </div>

      <label style={labelStyle}>Tên đề</label>
      <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} style={inputStyle} />

      {type === "synthetic" && (
        <>
          <label style={labelStyle}>Số câu (random mỗi lượt làm)</label>
          <input
            type="number"
            min={1}
            value={numQuestions}
            onChange={(e) => setNumQuestions(e.target.value)}
            style={inputStyle}
          />
        </>
      )}

      <div style={{ display: "flex", gap: 16 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Thời gian toàn đề (phút, để trống = không giới hạn)</label>
          <input
            type="number"
            min={1}
            value={timeLimitMinutes}
            onChange={(e) => setTimeLimitMinutes(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Thời gian mỗi câu (giây, để trống = không giới hạn)</label>
          <input
            type="number"
            min={1}
            value={timePerQuestionSeconds}
            onChange={(e) => setTimePerQuestionSeconds(e.target.value)}
            style={inputStyle}
          />
        </div>
      </div>

      {type === "fixed" && (
        <>
          <label style={labelStyle}>
            Chọn câu hỏi ({selectedIds.length} đã chọn / {availableQuestions.length} khả dụng — chỉ hiện câu hỏi
            chưa thuộc đề cố định nào khác)
          </label>
          <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 8, padding: 8 }}>
            {availableQuestions.length === 0 && (
              <p style={{ color: "#6b7280", fontSize: 13 }}>Chưa có câu hỏi khả dụng ở node này.</p>
            )}
            {availableQuestions.map((q) => (
              <label key={q.id} style={{ display: "flex", gap: 8, padding: "4px 0", fontSize: 13 }}>
                <input type="checkbox" checked={selectedIds.includes(q.id)} onChange={() => toggleSelect(q.id)} />
                {q.content}
              </label>
            ))}
          </div>
        </>
      )}

      {error && <p style={{ color: "#dc2626", fontSize: 13 }}>{error}</p>}
      {successMsg && <p style={{ color: "#16a34a", fontSize: 13 }}>{successMsg}</p>}

      <div style={{ marginTop: 16 }}>
        <button type="submit" disabled={saving} style={primaryBtnStyle}>
          {saving ? "Đang tạo..." : "Tạo đề"}
        </button>
      </div>
    </form>
  );
};

const cardStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 16,
  background: "#fff",
  maxWidth: 560,
};

const labelStyle = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "#374151",
  margin: "12px 0 4px",
};

const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  border: "1px solid #d1d5db",
  borderRadius: 8,
  fontSize: 14,
  boxSizing: "border-box",
};

const primaryBtnStyle = {
  padding: "8px 16px",
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 600,
};

export default Quiz3QuizForm;
